import { createContext, useContext, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, type Ref } from "react";
import { createPortal } from "react-dom";
import { Node, type Editor } from "@tiptap/core";
import { EditorContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor, type ReactNodeViewProps } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import { UndoRedo } from "@tiptap/extensions";
import { closeHistory } from "@tiptap/pm/history";
import useSWR from "swr";
import { X } from "lucide-react";
import { productionAssetsPath, swrFetcher, type ProductionAssetInfo } from "@/lib/api";
import { bindReference, directImageReferences, findActiveMentionToken, referenceBindings, type ReferenceBinding } from "@/lib/mentions";
import { promptDocument, promptText } from "@/lib/promptDocument";
import { cn } from "@/lib/utils";
import { ReferenceThumb } from "./ReferenceThumb";
import { ReferenceLightbox, type ReferencePreviewTarget } from "./ReferenceLightbox";

interface ReferenceContextValue {
  params: Record<string, unknown>;
  elements: ProductionAssetInfo[] | undefined;
  preview: (target: ReferencePreviewTarget) => void;
}
const ReferenceContext = createContext<ReferenceContextValue | null>(null);

function ReferenceNodeView({ node, deleteNode, editor }: ReactNodeViewProps) {
  const context = useContext(ReferenceContext)!;
  const token = String(node.attrs.token);
  const binding = referenceBindings(context.params).find((item) => item.token === token);
  const candidates = (context.elements ?? []).filter((item) => !item.archived && (binding ? binding.kind === "element" && item.id === binding.id : item.slug === token.slice(1)));
  const element = candidates.length === 1 ? candidates[0] : null;
  const image = binding?.kind === "asset" ? directImageReferences(context.params).find((item) => item.asset_id === binding.id) : null;
  const changed = Boolean(binding?.updated_at && element && binding.updated_at !== element.updated_at);
  const invalid = changed || (!element && !image && context.elements !== undefined);
  const ids = image ? [image.asset_id] : element?.canonical_reference_ids ?? [];
  const label = element?.name ?? image?.name ?? token;
  return <NodeViewWrapper as="span" contentEditable={false} data-reference-token={token}
    data-reference-status={invalid ? "unavailable" : !element && !image ? "preparing" : "selected"}
    className={cn("inline-flex max-w-full items-center gap-1 rounded-lg border bg-studio-panel px-1 align-middle text-xs leading-7", invalid ? "border-studio-warning text-studio-warning" : "border-studio-border text-studio-ink")}>
    {ids.map((id, index) => <button key={id} type="button" disabled={invalid} aria-label={`Preview ${label}${ids.length > 1 ? ` ${index + 1}` : ""}`}
      onMouseDown={(event) => event.preventDefault()} onClick={() => context.preview({ assetId: id, label })}
      className="inline-flex h-7 w-7 shrink-0 overflow-hidden rounded focus-visible:outline focus-visible:outline-studio-accent disabled:opacity-50">
      <ReferenceThumb assetId={id} label={`${label} ${index + 1}`} />
    </button>)}
    <button type="button" disabled={invalid || !ids.length} onMouseDown={(event) => event.preventDefault()}
      onClick={() => context.preview({ assetId: ids[0], label })} title={invalid ? "Reference changed or is unavailable. Select it again." : "Selected reference. Cost review checks provider compatibility."}
      className="max-w-[12rem] truncate text-left disabled:cursor-default">{label}{invalid ? " · unavailable" : !element && !image ? " · preparing" : ""}</button>
    <button type="button" aria-label={`Remove ${token} from prompt`} onMouseDown={(event) => event.preventDefault()} onClick={() => {
      editor.view.dispatch(closeHistory(editor.state.tr));
      deleteNode();
      editor.view.dispatch(closeHistory(editor.state.tr));
    }}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-studio-card"><X className="h-3 w-3" /></button>
  </NodeViewWrapper>;
}

const PromptReference = Node.create({
  name: "promptReference", group: "inline", inline: true, atom: true, selectable: true,
  addAttributes: () => ({ token: { default: "" } }),
  // Imported clipboard HTML is not trusted to create bindings; paste uses plain text.
  parseHTML: () => [],
  renderHTML: ({ node }) => ["span", { "data-reference-token": node.attrs.token }, node.attrs.token],
  renderText: ({ node }) => String(node.attrs.token),
  addNodeView: () => ReactNodeViewRenderer(ReferenceNodeView),
});

interface MentionOption { id: string; kind: "element" | "asset"; name: string; slug: string; updated_at?: string; imageId?: string }
interface MentionMenu { from: number; to: number; query: string; left: number; top?: number; bottom?: number }
export interface PromptEditorHandle { focus: () => void; mention: () => void }
interface Props {
  ref?: Ref<PromptEditorHandle>;
  value: string;
  params: Record<string, unknown>;
  onChange: (value: string, binding?: ReferenceBinding) => void;
  onSubmit?: () => void;
  name?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/** One editing surface; atoms serialize back to the existing @token wire format. */
export function PromptEditor({ ref, value, params, onChange, onSubmit, name = "prompt", label = "Creative prompt", placeholder, disabled = false, className }: Props) {
  const { data: elements } = useSWR<ProductionAssetInfo[]>(productionAssetsPath(undefined, undefined, false), swrFetcher);
  const [menu, setMenu] = useState<MentionMenu | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  const [preview, setPreview] = useState<ReferencePreviewTarget | null>(null);
  const listId = useId();
  const pendingBinding = useRef<ReferenceBinding | undefined>(undefined);
  const callbacks = useRef({ onChange, onSubmit, params });
  callbacks.current = { onChange, onSubmit, params };
  const keyboard = useRef<(event: KeyboardEvent) => boolean>(() => false);
  const lastQuery = useRef<string | undefined>(undefined);

  function updateMenu(instance: Editor) {
    if (!instance.isFocused || !instance.state.selection.empty || instance.view.composing) { setMenu(null); return; }
    const { $from } = instance.state.selection;
    // Each atom occupies one document position; don't use its longer wire token here.
    const text = $from.parent.textBetween(0, $from.parent.content.size, "\n", () => "\ufffc");
    const active = findActiveMentionToken(text, $from.parentOffset);
    if (!active) { setMenu(null); return; }
    const box = instance.view.dom.getBoundingClientRect();
    const above = box.top > 280 || window.innerHeight - box.bottom < 280;
    const next = { from: $from.start() + active.start, to: $from.start() + active.end, query: active.query,
      left: Math.max(8, Math.min(box.left, window.innerWidth - 296)),
      ...(above ? { bottom: window.innerHeight - box.top + 8 } : { top: box.bottom + 8 }) };
    if (lastQuery.current !== next.query) setHighlighted(0);
    lastQuery.current = next.query;
    setMenu(next);
  }

  const editor = useEditor({
    extensions: [Document.extend({ content: "paragraph" }), Paragraph, Text, HardBreak, UndoRedo, PromptReference],
    content: promptDocument(value), editable: !disabled,
    editorProps: {
      attributes: { role: "textbox", "aria-multiline": "true", "aria-label": label, "data-param-name": name,
        "data-placeholder": placeholder ?? "", class: cn("prompt-editor min-h-20 max-h-64 overflow-y-auto overscroll-contain whitespace-pre-wrap break-words text-sm leading-8 outline-none", className) },
      handleKeyDown: (_view, event) => keyboard.current(event),
      clipboardTextSerializer: (slice) => slice.content.textBetween(0, slice.content.size, "\n", (node) => node.type.name === "promptReference" ? String(node.attrs.token) : "\n"),
      handlePaste: (_view, event) => {
        const text = event.clipboardData?.getData("text/plain");
        if (text === undefined) return false;
        event.preventDefault();
        if (editor) {
          editor.view.dispatch(closeHistory(editor.state.tr));
          editor.commands.insertContent(promptDocument(text.replace(/\r\n?/g, "\n")).content![0].content!);
          editor.view.dispatch(closeHistory(editor.state.tr));
        }
        return true;
      },
      handleDrop: (_view, event) => { event.preventDefault(); return true; },
    },
    onUpdate: ({ editor: instance }) => {
      const binding = pendingBinding.current;
      pendingBinding.current = undefined;
      callbacks.current.onChange(promptText(instance.getJSON()), binding);
      updateMenu(instance);
    },
    onSelectionUpdate: ({ editor: instance }) => updateMenu(instance),
    onFocus: ({ editor: instance }) => updateMenu(instance),
    onBlur: () => setMenu(null),
  });

  const options = useMemo<MentionOption[]>(() => [
    ...directImageReferences(params).map((image) => ({ id: image.asset_id, kind: "asset" as const, name: image.name, slug: `image-${image.asset_id.slice(0, 8)}`, imageId: image.asset_id })),
    ...(elements ?? []).filter((item) => !item.archived).map((item) => ({ id: item.id, kind: "element" as const, name: item.name, slug: item.slug, updated_at: item.updated_at, imageId: item.canonical_reference_ids[0] })),
  ].filter((item) => !menu?.query || `${item.slug} ${item.name}`.toLowerCase().includes(menu.query.toLowerCase())).slice(0, 8), [params, elements, menu?.query]);

  function select(option: MentionOption) {
    if (!editor || !menu) return;
    const binding = bindReference(callbacks.current.params, { token: `@${option.slug}`, id: option.id, kind: option.kind, updated_at: option.updated_at });
    pendingBinding.current = binding;
    editor.view.dispatch(closeHistory(editor.state.tr));
    const after = editor.state.doc.textBetween(menu.to, Math.min(menu.to + 1, editor.state.doc.content.size), "\n", () => "\ufffc");
    editor.chain().focus().insertContentAt({ from: menu.from, to: menu.to }, [
      { type: "promptReference", attrs: { token: binding.token } }, ...(!/^\s/.test(after) ? [{ type: "text", text: " " }] : []),
    ]).run();
    editor.view.dispatch(closeHistory(editor.state.tr));
    setMenu(null);
  }
  keyboard.current = (event) => {
    if (event.isComposing || event.keyCode === 229) return false;
    if (menu) {
      if (event.key === "Escape") { setMenu(null); return true; }
      if (options.length && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
        setHighlighted((current) => (current + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length); return true;
      }
      if (options.length && (event.key === "Tab" || (event.key === "Enter" && !event.ctrlKey && !event.metaKey))) { select(options[highlighted] ?? options[0]); return true; }
    }
    if (event.key === "Enter") {
      if (event.ctrlKey || event.metaKey) callbacks.current.onSubmit?.();
      else editor?.commands.setHardBreak();
      return true;
    }
    return false;
  };

  useImperativeHandle(ref, () => ({
    focus: () => { editor?.commands.focus(); },
    mention: () => {
      if (!editor) return;
      const { $from } = editor.state.selection;
      const before = $from.parent.textBetween(0, $from.parentOffset, "\n", () => "\ufffc");
      editor.chain().focus().insertContent(before && !/\s$/.test(before) ? " @" : "@").run();
    },
  }), [editor]);

  useEffect(() => {
    if (!editor || value === promptText(editor.getJSON())) return;
    // A restore/model change or a removal from the attachment strip, not a local echo.
    const position = editor.state.selection.from;
    editor.commands.setContent(promptDocument(value), { emitUpdate: false });
    editor.commands.setTextSelection(Math.min(position, editor.state.doc.content.size - 1));
  }, [editor, value]);
  useEffect(() => { editor?.setEditable(!disabled); }, [editor, disabled]);
  useEffect(() => {
    if (!editor) return;
    editor.view.dom.setAttribute("aria-expanded", String(Boolean(menu)));
    if (menu) {
      editor.view.dom.setAttribute("aria-controls", listId);
      editor.view.dom.setAttribute("aria-activedescendant", `${listId}-${highlighted}`);
    } else {
      editor.view.dom.removeAttribute("aria-controls");
      editor.view.dom.removeAttribute("aria-activedescendant");
    }
  }, [editor, menu, listId, highlighted]);
  useEffect(() => {
    const close = (event: Event) => {
      if (event.target instanceof Element && event.target.closest("[data-prompt-mention-menu]")) return;
      setMenu(null);
    };
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => { window.removeEventListener("resize", close); window.removeEventListener("scroll", close, true); };
  }, []);

  return <ReferenceContext.Provider value={{ params, elements, preview: setPreview }}>
    <div className={cn("min-w-0", disabled && "opacity-55")}>
      <EditorContent editor={editor} />
      {menu && createPortal(<div id={listId} data-prompt-mention-menu role="listbox" aria-label="Mention a reference" style={{ left: menu.left, top: menu.top, bottom: menu.bottom }}
        onMouseDown={(event) => event.preventDefault()} className="fixed z-[70] max-h-64 w-72 max-w-[85vw] overflow-y-auto rounded-xl border border-studio-border bg-studio-panel p-1.5 shadow-panel">
        {!options.length && <p className="px-2 py-1.5 text-xs text-studio-muted">No matching references</p>}
        {options.map((option, index) => <button id={`${listId}-${index}`} key={`${option.kind}-${option.id}`} type="button" role="option" aria-selected={highlighted === index}
          onMouseEnter={() => setHighlighted(index)} onClick={() => select(option)} className={cn("flex w-full items-center gap-2 rounded-lg p-2 text-left", highlighted === index ? "bg-studio-accent-soft" : "hover:bg-studio-card")}>
          <span className="inline-flex h-7 w-7 shrink-0 overflow-hidden rounded">{option.imageId && <ReferenceThumb assetId={option.imageId} label={option.name} />}</span>
          <span className="min-w-0"><span className="block truncate text-xs font-semibold">{option.name}</span><span className="block text-[10px] text-studio-muted">{option.kind === "asset" ? "Direct image" : "Element"}</span></span>
        </button>)}
      </div>, document.body)}
      {preview && <ReferenceLightbox target={preview} onClose={() => setPreview(null)} />}
    </div>
  </ReferenceContext.Provider>;
}
